import { MouseEvent, TouchEvent, useEffect, useRef } from 'react';
import {
  HighlightedTextProps,
  OnCreateHighlightsT,
  OnUpdateHighlightsT,
} from './types';
import Markdown from '../markdown';
import HighlightLayer from './highlight-layer';
import { useHighlight } from '../../contexts/highlight-provider/useHighlight';
import HighlightTooltip from './highlight-tooltip';
import { cn } from '../../utils/common-utils';
import { HtmlElAltIdEnum } from '../../enums';
import { HighlightOperationEnum } from './highlight-tooltip/types';
import { createHighlightHtmlFromRange } from './utils/range-utils';
import { highlightTextCursor } from './highlight-cursor';

const TOUCH_SELECTION_SYNC_DELAY_MS = 80;

const HighlightedText = ({
  value,
  highlightParentKey,
  questionId,
  className,
  ...props
}: HighlightedTextProps) => {
  const {
    highlightData,
    highlightToggle,
    lastUsedColor,
    onAddHighlight,
    onUpdateHighlight,
    selectedHighlightKeyId,
    setSelectedHighlightKeyId,
    setTooltipPos,
    tooltipPos,
  } = useHighlight();

  const originalRef = useRef<HTMLDivElement>(null);
  const suppressClickUntilRef = useRef(0);
  const pendingTouchHighlightRef = useRef<number | null>(null);

  const selectionRef = useRef<{
    html: string;
    range: Range | null;
  }>({ html: '', range: null });
  const lastValidSelectionRef = useRef<{
    html: string;
    range: Range | null;
  }>({ html: '', range: null });

  const syncSelectionRef = () => {
    const selection = window.getSelection();
    const container = originalRef.current;

    if (
      !selection ||
      selection.rangeCount === 0 ||
      selection.isCollapsed ||
      !container
    ) {
      selectionRef.current = { html: '', range: null };
      return null;
    }

    const range = selection.getRangeAt(0);

    if (
      !container.contains(range.startContainer) ||
      !container.contains(range.endContainer)
    ) {
      selectionRef.current = { html: '', range: null };
      return null;
    }

    const cloned = range.cloneContents();
    const div = document.createElement('div');
    div.appendChild(cloned);

    const html = div.innerHTML.replace(/\r?\n|\r/g, '');
    const clonedRange = range.cloneRange();

    selectionRef.current = {
      html,
      range: clonedRange,
    };
    lastValidSelectionRef.current = {
      html,
      range: clonedRange,
    };

    return selectionRef.current;
  };

  const hasSelectionWithinContainer = () => {
    const selection = window.getSelection();
    const container = originalRef.current;

    return (
      !!selection &&
      selection.rangeCount > 0 &&
      !selection.isCollapsed &&
      !!container &&
      container.contains(selection.getRangeAt(0).startContainer) &&
      container.contains(selection.getRangeAt(0).endContainer)
    );
  };

  useEffect(() => {
    const handler = () => {
      syncSelectionRef();
    };

    document.addEventListener('selectionchange', handler);
    return () => {
      document.removeEventListener('selectionchange', handler);
    };
  }, []);

  useEffect(
    () => () => {
      if (pendingTouchHighlightRef.current) {
        window.clearTimeout(pendingTouchHighlightRef.current);
      }
    },
    [],
  );

  const onUpdateHighlights = (data: OnUpdateHighlightsT) => {
    onUpdateHighlight({
      ...data,
      keyId: selectedHighlightKeyId,
    });
  };

  const createHighlightFromSelection = (data: OnCreateHighlightsT): boolean => {
    const currentSelection = selectionRef.current.range
      ? selectionRef.current
      : lastValidSelectionRef.current.range
        ? lastValidSelectionRef.current
        : syncSelectionRef();
    const range = currentSelection?.range;

    if (!range) return false;

    const container = originalRef.current;
    if (!container) return false;

    const originalHtml = container.innerHTML.replace(/\r?\n|\r/g, '');
    const keyId = `${highlightParentKey}_${highlightData.length + 1}`;

    const highlightedHtml = createHighlightHtmlFromRange(
      container,
      range,
      keyId,
    );

    if (!highlightedHtml) {
      console.error('Failed to create highlight from range');
      return false;
    }

    onAddHighlight({
      ...data,
      keyId,
      questionId,
      content: originalHtml,
      highlightedHtml,
      sticker: {
        title: container,
        isNeedSticker: false,
        ...data?.sticker,
      },
    });

    window.getSelection()?.removeAllRanges();
    selectionRef.current = { html: '', range: null };
    lastValidSelectionRef.current = { html: '', range: null };

    return true;
  };

  const onCreateHighlights = (data: OnCreateHighlightsT) => {
    createHighlightFromSelection(data);
  };

  const showQuickHighlightTooltip = (tooltipCoordinates: {
    left: number;
    top: number;
  }) => {
    const currentSelection = syncSelectionRef();

    if (!currentSelection?.range && !lastValidSelectionRef.current.range)
      return;

    suppressClickUntilRef.current = Date.now() + 250;
    setSelectedHighlightKeyId('');

    const didCreateHighlight = createHighlightFromSelection({
      color: lastUsedColor,
      underLine: null!,
    });

    if (!didCreateHighlight) return;

    setTooltipPos({
      ...tooltipCoordinates,
      highlightParentKey,
    });
  };

  const handleTouchStartCapture = () => {
    if (!highlightToggle || hasSelectionWithinContainer()) return;

    selectionRef.current = { html: '', range: null };
    lastValidSelectionRef.current = { html: '', range: null };
  };

  const handleQuickHighlightSelectionEnd = (
    event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
  ) => {
    if (!highlightToggle) return;

    const tooltipCoordinates =
      'changedTouches' in event
        ? {
            left: event.changedTouches[0]?.clientX ?? 0,
            top: (event.changedTouches[0]?.clientY ?? 0) + 50,
          }
        : {
            left: event.clientX,
            top: event.clientY + 50,
          };

    if ('changedTouches' in event) {
      const currentSelection = syncSelectionRef();

      if (!currentSelection?.range && !lastValidSelectionRef.current.range) {
        if (pendingTouchHighlightRef.current) {
          window.clearTimeout(pendingTouchHighlightRef.current);
        }

        pendingTouchHighlightRef.current = window.setTimeout(() => {
          pendingTouchHighlightRef.current = null;
          showQuickHighlightTooltip(tooltipCoordinates);
        }, TOUCH_SELECTION_SYNC_DELAY_MS);

        return;
      }

      showQuickHighlightTooltip(tooltipCoordinates);
      event.stopPropagation();

      return;
    }

    const currentSelection = syncSelectionRef();

    if (!currentSelection?.range) return;

    showQuickHighlightTooltip(tooltipCoordinates);
    event.stopPropagation();
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;

    if (
      target?.closest(`[data-alt-type="${HtmlElAltIdEnum.HIGHLIGHT_TOOLTIP}"]`)
    ) {
      return;
    }

    if (
      Date.now() > suppressClickUntilRef.current &&
      !hasSelectionWithinContainer()
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className={cn(
        'highlight-selection-surface relative font-gentium text-base',
        className,
      )}
      data-alt-parent-key={highlightParentKey}
      data-alt-type={HtmlElAltIdEnum.HIGHLIGHT_PARENT}
      onClickCapture={handleClickCapture}
      onMouseUpCapture={handleQuickHighlightSelectionEnd}
      onTouchEndCapture={handleQuickHighlightSelectionEnd}
      onTouchStartCapture={handleTouchStartCapture}
      {...props}
    >
      <div
        className={'pointer-events-none select-none opacity-0'}
        key={'vanish_text'}
        style={{ bottom: '-0.5rem' }}
      >
        <Markdown>{value}</Markdown>
      </div>

      <div
        ref={originalRef}
        className={cn(
          'absolute left-0 top-0 z-50 h-full w-full',
          highlightToggle && 'select-text',
        )}
        key={'original_text'}
        style={{
          bottom: '-0.5rem',
          cursor: highlightToggle ? highlightTextCursor : undefined,
          WebkitUserSelect: highlightToggle ? 'text' : undefined,
          userSelect: highlightToggle ? 'text' : undefined,
        }}
      >
        <Markdown>{value}</Markdown>
      </div>

      {highlightData
        .filter((hl) => hl.keyId.startsWith(highlightParentKey))
        .map((hl, i) => (
          <HighlightLayer key={hl.keyId + i} highlight={hl} zIndex={i} />
        ))}

      {tooltipPos?.highlightParentKey === highlightParentKey && (
        <>
          {!selectedHighlightKeyId && (
            <HighlightTooltip
              onMutateHighlight={onCreateHighlights}
              highlightOperation={HighlightOperationEnum.CREAT}
            />
          )}

          {selectedHighlightKeyId.startsWith(highlightParentKey) && (
            <HighlightTooltip
              onMutateHighlight={onUpdateHighlights}
              highlightOperation={HighlightOperationEnum.UPDATE}
            />
          )}
        </>
      )}
    </div>
  );
};

export default HighlightedText;
